/**
 * Domain hook for Drills module data management - Anchor Mode v2.0 compliant
 * Centralizes all drills-related storage and business logic
 * 
 * Base key: 'drills' (vessel scoping handled by useStorageState)
 * RBAC-gated mutations
 * No vesselId in records
 * One write per save
 * 
 * FT-112: Compliance now uses drillList from useDrillsConfig (WBC3 + company drills)
 * 
 * @example
 * const { drills, saveDrill, deleteDrill, getOverdueDrills } = useDrillsData();
 */

import { useCallback } from 'react';
import { useStorageState } from '../useStorageState';
import { useVessel } from '../../contexts/VesselContext';
import { usePermissions } from '../usePermissions';
// Use the config version which has the proper drill_type fields
import { getDrillRequirements, DRILL_TYPES } from '../../config/drillRequirements';
// Import drills config for required drills list (single source of truth)
import { useDrillsConfig } from './drills/useDrillsConfig';
// Keep useDrillRegister for tracking completion state (lastCompleted, nextDue)
import { useDrillRegister } from './useDrillRegister';
// Import shared helpers for consistent calculations with SafetyDrillsTab
import { calculateDrillStats, calculateDrillStatus } from '../../components/tailwind/modules/drills/utils/drillsHelpers';
// FT-458: soft-delete tombstone helper — exclude deleted drills from compliance reads.
import { filterTombstoned } from '../../sync/tombstone';

// RBAC multi-signature shim
const canDo = (can, resource, action) => {
  try {
    if (!can || typeof can !== 'function') return false;
    return (
      (can.length >= 2 && can(resource, action)) ||
      can(`${resource}:${action}`) ||
      can({ resource, action }) ||
      can(action, resource)
    );
  } catch { 
    return false; 
  }
};

// Fallback drill types if config unavailable
const FALLBACK_DRILL_TYPES = [
  { id: 'fire', label: 'Fire Drill' },
  { id: 'abandon_ship', label: 'Abandon Ship' },
  { id: 'man_overboard', label: 'Man Overboard' }
];

export const useDrillsData = () => {
  const { currentVesselId, getCurrentVessel } = useVessel();
  const currentVessel = getCurrentVessel();
  const { can, currentUser } = usePermissions();
  const userName = currentUser?.name || 'Unknown User';
  
  // Base key only - vessel scoping handled by useStorageState
  const { value: drills = [], setValue: setDrills } = useStorageState('drills', []);
  // FT-458: compliance-facing view with soft-deleted (tombstoned) drills removed.
  // READ getters (overdue/upcoming/compliance/register + the exposed `drills`
  // property consumed by dashboards) use this so a deleted drill can't be read
  // as a completion and mask an overdue/compliance gap. WRITE functions
  // (saveDrill/updateDrill/approve/deleteDrill) keep the RAW `drills` so the
  // tombstone is preserved on the array we persist.
  const visibleDrills = filterTombstoned(drills);
  const { value: onboardTraining = [], setValue: setOnboardTraining } = useStorageState('onboard_training', []);
  const { value: customDrills = [], setValue: setCustomDrills } = useStorageState('customDrills', []);
  
  // FT-112: Get drill list from config (single source of truth for which drills are required)
  const { drillList } = useDrillsConfig();

  // Keep useDrillRegister for tracking completion state (lastCompleted, nextDue)
  const { drillRegister, getDrillRegister, updateDrill } = useDrillRegister();
  
  // Get vessel-specific requirements (keep for backward compatibility)
  const requirements = currentVessel ? getDrillRequirements(currentVessel) : {};
  
  // Expose drill types for dropdowns
  const drillTypes = DRILL_TYPES ? 
    Object.entries(DRILL_TYPES).map(([key, value]) => ({
      id: value,
      label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    })) : FALLBACK_DRILL_TYPES;
  
  // Normalize drill requirement from various sources
  const normalizeDrillRequirement = (req) => {
    if (!req) return null;
    
    return {
      id: req.drill_type || req.type || req.id,
      title: req.name || req.title || req.drill_type,
      frequency: req.frequency_days || req.frequency || 30,
      required: req.required === true || req.compliance_level === 'Required',
      compliance_level: req.compliance_level || (req.required ? 'Required' : 'Recommended'),
      citation: req.regulatory_source || req.citation || ''
    };
  };
  
  
  // Save a new drill record - RBAC-gated
  const saveDrill = useCallback(async (drillData) => {
    if (!canDo(can, 'drills', 'create')) {

      return null;
    }

    const now = new Date().toISOString();

    const newDrill = {
      ...drillData,
      id: drillData.id || `DRILL-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      timestamp: Date.now(), // Canonical: epoch milliseconds
      // NO vesselId - Anchor Mode v2.0 compliance

      // v4.2 Audit Trail (camelCase) - FT-06b: Object format for audit trail
      createdBy: {
        userId: currentUser?.id,
        name: currentUser?.name,
        role: currentUser?.role
      },
      createdAt: now,
      updatedBy: null,
      updatedAt: null,

      // v4.2 Special: Drills conducted_by tracking (camelCase)
      conductedBy: drillData.conductedBy || drillData.participants?.map(p => p.name || p.id).join(', ') || userName,
      conductedAt: drillData.date || now,

      // v4.2 Days 11-12: Review/Approval fields (WBC3 SMS)
      // FT-06e: Store reviewedByRole for "Name (Role)" display format
      // FT-394: reviewStatus always starts as 'pending' — sign-off is now mandatory, not optional.
      //         Callers may pass reviewStatus: 'approved' (via signOffNow path in DrillsModule)
      //         to immediately sign off at creation time; otherwise default to 'pending'.
      reviewedBy: drillData.reviewedBy ?? null,
      reviewedByRole: drillData.reviewedByRole ?? null,
      reviewedAt: drillData.reviewedAt ?? null,
      reviewStatus: drillData.reviewStatus ?? 'pending', // FT-394: default 'pending', not null
      reviewComments: drillData.reviewComments ?? null
    };

    // ONE write per save
    const updated = [...drills, newDrill];
    await setDrills(updated);
    
    // UPDATE: Also update drill register with completion info
    const register = getDrillRegister();
    const drillInRegister = register.find(d => d.drillType === drillData.type);
    if (drillInRegister) {
      const drillDate = new Date(drillData.date);
      const nextDue = new Date(drillDate.getTime() + (drillInRegister.frequencyDays * 24 * 60 * 60 * 1000));
      
      updateDrill(drillInRegister.id, {
        lastCompleted: drillDate.toISOString(),
        nextDue: nextDue.toISOString(),
        lastCompletedBy: drillData.participants?.map(p => p.name || p.id).join(', ') || 'Unknown'
      });
    }
    
    return newDrill.id;
  }, [can, drills, setDrills, getDrillRegister, updateDrill]);

  // Update an existing drill record - RBAC-gated
  const updateDrillRecord = useCallback(async (drillId, updates) => {
    if (!canDo(can, 'drills', 'update')) {
      return null;
    }

    const now = new Date().toISOString();

    const updated = drills.map(d => {
      if (d.id === drillId) {
        return {
          ...d,
          ...updates,

          // v4.2 Audit Trail (camelCase) - FT-06c: Object format for updatedBy
          updatedBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role },
          updatedAt: now
        };
      }
      return d;
    });

    await setDrills(updated);
    return true;
  }, [can, drills, setDrills, userName]);

/**
 * Approve a drill record (WBC3 Appendix 8 - SMS review requirements)
 */
const approveDrill = useCallback(async (drillId, comments = '') => {
  if (!canDo(can, 'drills', 'update')) {
    return false;
  }

  const now = new Date().toISOString();

  const updated = drills.map(d => {
    if (d.id === drillId) {
      return {
        ...d,
        reviewedBy: userName,
        reviewedByRole: currentUser?.role || '',  // FT-06e: Store role for "Name (Role)" display
        reviewedAt: now,
        reviewStatus: 'approved',
        reviewComments: comments || '',
        // FT-06c: Object format for updatedBy
        updatedBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role },
        updatedAt: now
      };
    }
    return d;
  });

  const approvedDrill = updated.find(d => d.id === drillId);
  await setDrills(updated);
  return approvedDrill || false;
}, [can, drills, setDrills, userName]);

/**
 * Reject a drill record (WBC3 Appendix 8 - SMS review requirements)
 */
const rejectDrill = useCallback(async (drillId, reason = '') => {
  if (!canDo(can, 'drills', 'update')) {
    return false;
  }

  const now = new Date().toISOString();

  const updated = drills.map(d => {
    if (d.id === drillId) {
      return {
        ...d,
        reviewedBy: userName,
        reviewedByRole: currentUser?.role || '',  // FT-06e: Store role for "Name (Role)" display
        reviewedAt: now,
        reviewStatus: 'rejected',
        reviewComments: reason || '',
        // FT-06c: Object format for updatedBy
        updatedBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role },
        updatedAt: now
      };
    }
    return d;
  });

  await setDrills(updated);
  return true;
}, [can, drills, setDrills, userName]);

  // Delete a drill record - RBAC-gated
  const deleteDrill = useCallback(async (drillId) => {
    if (!canDo(can, 'drills', 'delete')) {

      return false;
    }
    
    // FT-458: soft-delete via tombstone — stamp deleted_at + KEEP in array so the
    // deletion propagates through the cloud∪local sync union (a hard filter lets
    // the cloud copy resurrect it). Mirrors the live DrillsModule delete + FT-175
    // crew. (This hook's deleteDrill isn't wired to any live UI, but is fixed
    // defensively so a future wiring can't reintroduce the resurrection bug.)
    const nowIso = new Date().toISOString();
    const updated = drills.map(d =>
      d.id === drillId
        ? { ...d, deleted_at: nowIso, deleted_by: userName, updatedAt: Date.now() }
        : d
    );
    await setDrills(updated);
    return true;
  }, [can, drills, setDrills, userName]);
  
  // FT-112: Get overdue drills based on drillList from useDrillsConfig
  const getOverdueDrills = () => {
    const overdue = [];
    const today = new Date();

    // FT-112: Use drillList as source of truth (WBC3 mandatory + company required)
    // All items in drillList are required, no need to filter by complianceLevel
    (drillList || []).forEach((drill) => {
      // drillList items are always required (WBC3 + company drills)
      
      // Check by nextDue date if available (preferred method)
      if (drill.nextDue) {
        const nextDueDate = new Date(drill.nextDue);
        if (nextDueDate < today) {
          const daysOverdue = Math.floor((today - nextDueDate) / (1000 * 60 * 60 * 24));
          overdue.push({
            type: drill.drillType,
            name: drill.name,
            daysOverdue,
            lastPerformed: drill.lastCompleted ? new Date(drill.lastCompleted) : null,
            nextDue: nextDueDate
          });
        }
      } else {
        // Fallback: Calculate based on last completed and frequency
        const lastDrill = visibleDrills // FT-458: ignore soft-deleted drills
          .filter(d => d.type === drill.drillType)
          .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
        
        if (!lastDrill) {
          overdue.push({ 
            type: drill.drillType,
            name: drill.name, 
            daysOverdue: 'Never performed' 
          });
        } else {
          const lastDate = new Date(lastDrill.date);
          const daysSince = Math.floor((today - lastDate) / (1000 * 60 * 60 * 24));
          
          if (daysSince > drill.frequencyDays) {
            overdue.push({ 
              type: drill.drillType,
              name: drill.name,
              daysOverdue: daysSince - drill.frequencyDays,
              lastPerformed: lastDate
            });
          }
        }
      }
    });
    
    return overdue;
  };
  
  // FT-112: Get upcoming drills (due within specified days)
  const getUpcomingDrills = (daysAhead = 7) => {
    const upcoming = [];
    const today = new Date();

    // FT-112: Use drillList as source of truth (WBC3 mandatory + company required)
    (drillList || []).forEach((drill) => {
      // All drillList items are required, no need to filter
      
      // Check by nextDue date if available
      if (drill.nextDue) {
        const nextDueDate = new Date(drill.nextDue);
        const daysUntilDue = Math.floor((nextDueDate - today) / (1000 * 60 * 60 * 24));
        
        // Include if due within specified days ahead but not overdue
        if (daysUntilDue >= 0 && daysUntilDue <= daysAhead) {
          upcoming.push({
            drill_type: drill.drillType,
            name: drill.name,
            daysUntilDue,
            nextDue: nextDueDate,
            lastPerformed: drill.lastCompleted ? new Date(drill.lastCompleted) : null
          });
        }
      } else {
        // Fallback: Calculate based on last completed and frequency
        const lastDrill = visibleDrills // FT-458: ignore soft-deleted drills
          .filter(d => d.type === drill.drillType)
          .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
        
        if (lastDrill) {
          const lastDate = new Date(lastDrill.date);
          const nextDueDate = new Date(lastDate.getTime() + (drill.frequencyDays * 24 * 60 * 60 * 1000));
          const daysUntilDue = Math.floor((nextDueDate - today) / (1000 * 60 * 60 * 24));
          
          if (daysUntilDue >= 0 && daysUntilDue <= daysAhead) {
            upcoming.push({
              drill_type: drill.drillType,
              name: drill.name,
              daysUntilDue,
              nextDue: nextDueDate,
              lastPerformed: lastDate
            });
          }
        }
      }
    });
    
    // Sort by due date (soonest first)
    return upcoming.sort((a, b) => a.daysUntilDue - b.daysUntilDue);
  };
  
  // Get drill compliance percentage using shared calculateDrillStats helper
  // This ensures Dashboard and SafetyDrillsTab show identical compliance percentages
  const getCompliancePercentage = () => {
    try {
      if (!drillList || !Array.isArray(drillList) || drillList.length === 0) {
        return 100; // No drills configured = 100% compliant
      }

      // Build drillStatuses array matching SafetyDrillsTab format
      const drillStatuses = drillList.map(drill => {
        const drillsOfType = visibleDrills.filter(d => d.type === drill.drillType); // FT-458: ignore soft-deleted
        const statusInfo = calculateDrillStatus(
          { frequency_days: drill.frequencyDays },
          drillsOfType
        );

        return {
          ...drill,
          status: statusInfo.status,
          complianceLevel: drill.complianceLevel || 'Required'
        };
      });

      // Use shared helper for consistent calculation
      const stats = calculateDrillStats(drillStatuses);
      return stats.compliance;
    } catch (error) {
      return 0; // Return 0 on error to be conservative
    }
  };
  
  // Get drill status for a specific drill type (moved from component hook)
  const getDrillStatus = useCallback((drillType) => {
    // Add null checks as requested by review agent
    if (!requirements || !drills) return null;
    
    const requirement = requirements[drillType];
    if (!requirement) return null;

    const frequencyDays = requirement.frequency_days;
    if (!frequencyDays) return { status: 'optional', requirement };

    // Find most recent drill of this type (FT-458: ignore soft-deleted)
    const recentDrill = visibleDrills
      .filter(drill => drill.type === drillType)
      .sort((a, b) => new Date(b.date) - new Date(a.date))[0];

    if (!recentDrill) {
      return {
        status: 'overdue',
        requirement,
        message: 'No drill recorded',
        daysSince: null,
        nextDue: new Date()
      };
    }

    const drillDate = new Date(recentDrill.date);
    const daysSince = Math.floor((Date.now() - drillDate.getTime()) / (1000 * 60 * 60 * 24));
    const nextDue = new Date(drillDate.getTime() + (frequencyDays * 24 * 60 * 60 * 1000));
    const daysUntilDue = Math.floor((nextDue.getTime() - Date.now()) / (1000 * 60 * 60 * 24));

    if (daysUntilDue < 0) {
      return {
        status: 'overdue',
        requirement,
        message: `Overdue by ${Math.abs(daysUntilDue)} days`,
        daysSince,
        nextDue,
        lastDrill: recentDrill
      };
    } else if (daysUntilDue <= 7) {
      return {
        status: 'due_soon',
        requirement,
        message: `Due in ${daysUntilDue} days`,
        daysSince,
        nextDue,
        lastDrill: recentDrill
      };
    } else {
      return {
        status: 'current',
        requirement,
        message: `Next due in ${daysUntilDue} days`,
        daysSince,
        nextDue,
        lastDrill: recentDrill
      };
    }
  }, [drills, requirements]); // Fixed dependency array per review agent
  
  // Format next due date (moved from component hook)
  const formatNextDue = useCallback((nextDueDate) => {
    try {
      if (!nextDueDate) return "—";

      const due = new Date(nextDueDate);
      if (isNaN(due.getTime())) return "—";

      const now = new Date();
      const daysUntilDue = Math.floor((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

      if (daysUntilDue < 0) {
        return `${Math.abs(daysUntilDue)} days overdue`;
      } else if (daysUntilDue <= 30) {
        return `in ${daysUntilDue} days`;
      } else {
        return due.toLocaleDateString();
      }
    } catch (error) {

      return "—";
    }
  }, []);
  
  // FT-112: Get quick stats for status cards using drillList from useDrillsConfig
  const getQuickStats = useCallback(() => {
    let overdueCount = 0;
    let dueSoonCount = 0;
    let nextDrill = null;
    let completionRate = 0;

    const today = new Date();
    const sevenDaysFromNow = new Date(today.getTime() + (7 * 24 * 60 * 60 * 1000));

    // FT-112: Use drillList as source of required drills (WBC3 + company drills)
    const requiredDrills = drillList || [];

    requiredDrills.forEach((drill) => {
      // Use nextDue from register if available
      if (drill.nextDue) {
        const nextDueDate = new Date(drill.nextDue);
        
        if (nextDueDate < today) {
          overdueCount++;
        } else if (nextDueDate <= sevenDaysFromNow) {
          dueSoonCount++;
        }
        
        // Track soonest drill
        if (!nextDrill || nextDueDate < new Date(nextDrill.date)) {
          nextDrill = {
            type: drill.drillType,
            name: drill.name,
            date: nextDueDate
          };
        }
      } else {
        // Fallback to calculating from drill history
        const status = getDrillStatus(drill.drillType);
        if (!status) return;

        if (status.status === 'overdue') overdueCount++;
        else if (status.status === 'due_soon') dueSoonCount++;

        // Find next drill due
        if (status.nextDue && (!nextDrill || status.nextDue < nextDrill.date)) {
          nextDrill = {
            type: drill.drillType,
            name: drill.name,
            date: status.nextDue,
            daysUntil: Math.floor((status.nextDue.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
          };
        }
      }
    });

    // Calculate completion rate for last 30 days
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    // FT-458: count only non-tombstoned drills toward the completion rate — a
    // soft-deleted drill must not inflate the recent-completion count.
    const recentDrills = visibleDrills.filter(d => new Date(d.date) > thirtyDaysAgo).length;
    completionRate = requiredDrills.length > 0 ? Math.round((recentDrills / requiredDrills.length) * 100) : 0;

    // Onboard training stats
    const recentTraining = onboardTraining.filter(t => {
      const trainingDate = new Date(t.date);
      return trainingDate > thirtyDaysAgo;
    });

    return {
      overdueCount,
      dueSoonCount,
      nextDrill,
      completionRate,
      totalDrills: visibleDrills.length, // FT-458: exclude soft-deleted from the total-drills stat
      totalOnboardTraining: onboardTraining.length,
      trainingThisMonth: recentTraining.length
      // Note: activeCrew removed per brief - crew is separate domain
    };
  }, [drills, onboardTraining, drillList, getDrillStatus]);
  
  return {
    // Data
    drills, // RAW (incl. tombstones) — the mounted DrillsModule needs this for
            // its writes/delete-stamp + next-ID generation, and filters its own
            // display via visibleDrills. Do NOT filter here or writes lose tombstones.
    visibleDrills, // FT-458: display/count view (tombstoned removed) for dashboards
    onboardTraining,
    customDrills,
    requirements,
    
    // NEW: Drill register data
    drillRegister,
    getDrillRegister,
    
    // Actions
    saveDrill,
    updateDrill: updateDrillRecord,
    approveDrill,  // v4.2 Days 11-12
    rejectDrill,   // v4.2 Days 11-12
    deleteDrill,
    setDrills,
    setOnboardTraining,
    setCustomDrills,
    
    // Computed - Analytics
    getOverdueDrills,
    getUpcomingDrills,
    getCompliancePercentage,
    
    // Computed - Presentation (consolidated from component hook)
    getDrillStatus,
    formatNextDue,
    getQuickStats,
    
    // Adapter exports
    drillTypes,
    normalizeDrillRequirement
  };
};